import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, ShieldCheck, Store, Globe, PhoneCall, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");

  const subscribe = () => {
    if (!/\S+@\S+\.\S+/.test(email)) {
      toast.error("Enter a valid email to subscribe");
      return;
    }
    toast.success("Subscribed! Offers and health updates coming your way.");
    setEmail("");
  };

  return (
    <footer className="bg-emerald-950 text-emerald-50 mt-20" data-testid="site-footer">
      {/* order channels strip */}
      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
          <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-300">Also order via</span>
          {[["Website", Globe], ["Phone Order", PhoneCall], ["WhatsApp", Mail], ["Bulk Enquiry", Store]].map(([label, Icon]) => (
            <span key={label} className="flex items-center gap-2 text-sm font-semibold text-emerald-100/90">
              <Icon size={15} className="text-amber-400" /> {label}
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-10">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="bg-white p-1.5 rounded-xl inline-flex shadow-md border border-slate-100">
              <img src="/logo.jpeg" alt="Kanan Remedies logo" className="h-9 w-9 rounded-lg object-cover" />
            </span>
            <span className="font-extrabold text-lg tracking-tight">Kanan<span className="text-amber-400">Remedies</span></span>
          </div>
          <p className="text-sm text-emerald-100/70 leading-relaxed mt-4">Kanan Remedies brings clinically verified, WHO-GMP manufactured medicines and wellness formulations directly to licensed trade — with DL & GST verified compliance on every order.</p>
          <div className="flex items-start gap-2 mt-5 text-xs text-emerald-100/60">
            <ShieldCheck size={14} className="text-amber-400 mt-0.5 shrink-0" />
            <span>Rx medicines supplied only to verified Drug License holders with GST verification, per the Drugs and Cosmetics Act. Strictly B2B trade.</span>
          </div>
        </div>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-300 mb-4">Shop</p>
          <div className="space-y-2.5 text-sm text-emerald-100/80">
            <Link to="/shop" className="block hover:text-amber-400 transition-colors">All Products</Link>
            <Link to="/about" data-testid="footer-about-link" className="block hover:text-amber-400 transition-colors">About Us</Link>
            <Link to="/contact" data-testid="footer-contact-link" className="block hover:text-amber-400 transition-colors">Contact Us</Link>
            <Link to="/shop?rx=true" className="block hover:text-amber-400 transition-colors">Prescription (Rx)</Link>
            <Link to="/shop?category=Supplements" className="block hover:text-amber-400 transition-colors">Supplements</Link>
            <Link to="/orders" className="block hover:text-amber-400 transition-colors">Track Your Order</Link>
            <Link to="/login" className="block hover:text-amber-400 transition-colors">My Account</Link>
          </div>
        </div>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-300 mb-4">Legal & Policies</p>
          <div className="space-y-2.5 text-sm text-emerald-100/80">
            <Link to="/policies/privacy" data-testid="footer-privacy-link" className="block hover:text-amber-400 transition-colors">Privacy Policy</Link>
            <Link to="/policies/terms" data-testid="footer-terms-link" className="block hover:text-amber-400 transition-colors">Terms & Conditions</Link>
            <Link to="/policies/refund" data-testid="footer-refund-link" className="block hover:text-amber-400 transition-colors">Refund & Return Policy</Link>
            <Link to="/policies/shipping" data-testid="footer-shipping-link" className="block hover:text-amber-400 transition-colors">Shipping & Delivery</Link>
          </div>
        </div>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-300 mb-4">Contact</p>
          <div className="space-y-2.5 text-sm text-emerald-100/80">
            <span className="flex items-start gap-2 text-xs leading-relaxed"><MapPin size={14} className="text-amber-400 mt-0.5 shrink-0" /> B-316, Crystal Plaza, Opp. Infinity Mall, Link Road, Andheri West, Mumbai-400053</span>
            <span className="flex items-center gap-2 text-xs"><Mail size={14} className="text-amber-400 shrink-0" /> info@kananremedies.com</span>
            <span className="flex items-center gap-2 text-xs"><PhoneCall size={14} className="text-amber-400 shrink-0" /> +91 8852832320</span>
            <span className="flex items-start gap-2 text-[11px] text-emerald-100/60 font-mono-ed"><FileText size={14} className="text-amber-400 mt-0.5 shrink-0" /> DL: 24-25/118778-79<br />GST: 08CEDPS6160N1ZW</span>
          </div>
        </div>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-300 mb-4">Newsletter</p>
          <p className="text-sm text-emerald-100/70 leading-relaxed">Latest offers, new formulations and trade schemes — straight to your inbox.</p>
          <div className="flex gap-2 mt-4">
            <input
              data-testid="newsletter-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              className="flex-1 min-w-0 bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-sm placeholder:text-emerald-100/40 focus:outline-none focus:border-amber-400 transition-colors"
            />
            <button
              data-testid="newsletter-subscribe-btn"
              onClick={subscribe}
              className="bg-amber-400 hover:bg-amber-300 text-emerald-950 font-bold text-sm px-5 py-2.5 rounded-xl transition-all active:scale-95"
            >
              Join
            </button>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-5 flex flex-col sm:flex-row justify-between gap-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-100/50">
          <span>© 2026 Kanan Remedies Pvt. Ltd. | All rights reserved</span>
          <span>WHO-GMP Certified • 100% Authentic • Strictly B2B</span>
        </div>
      </div>
    </footer>
  );
}

